import React, { useContext, useState } from 'react'
import { Trans } from 'react-i18next';
import usePrompts from 'utils/hooks/usePrompts';
import PromptItem from './PromptItem';
import { Button } from 'components/ui';
import { useNavigate } from 'react-router-dom';
import { BsPlusCircle } from 'react-icons/bs';
import { CompanyPromptsContext } from 'contexts/CompanyPromptsContext';
import { TbAlertCircle, TbCircleCheck, TbSettings } from 'react-icons/tb';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';

const Prompts = ({
    network,
    selected = [],
    onSelect = () => void 0,
    onConfirm = () => void 0,
    isModal = false,
    prompts, 
    onClose = () => void 0,
}) => {
    const { 
        loading, 
        error, 
        fetchCompanyPrompts
    } = usePrompts();
    const { createPrompt } = useContext(CompanyPromptsContext);
    const [ isCreating, setIsCreating ] = useState(false);
    const navigate = useNavigate();
    const { whitelabel } = useWhitelabel();

    const selectable = isModal || !!selected.length;

    const handleAddPrompt = async (name = null, prompt = null) => {
        setIsCreating(true);
        try {
            await createPrompt(network, name, prompt);
        } catch (error) {
            console.error('Error creating prompt:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleSelect = (prompt) => {
        onSelect(prompt);
        // if (selected.find(p => p.id === prompt.id)) {
        //     onSelect(selected.filter(p => p.id !== prompt.id));
        // } else {
        //     onSelect([...selected, prompt]);
        // }
    };

    if (loading && !prompts.length) {
        return <div className="p-4">Loading...</div>;
    }

    if (error) {
        return <div className="p-4 text-red-500">Error loading prompts</div>;
    }

    if (!loading && !prompts.length) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center text-tertiary-500 dark:text-gray-400 dark:text-gray-400">
                <TbAlertCircle className="w-12 h-12 mb-4" />
                <h4 className="text-lg font-semibold mb-2">
                    <Trans i18nKey="prompts.noPromptsTitle">No prompts available</Trans>
                </h4>
                <p className="mb-4">
                    <Trans i18nKey="prompts.noPromptsDescription">
                        There are no prompts configured for {network}.
                    </Trans>
                </p>
                <Button
                    variant="solid"
                    size="sm"
                    icon={<BsPlusCircle />}
                    onClick={() => handleAddPrompt()}
                    loading={isCreating}
                    disabled={isCreating}
                >
                    <Trans i18nKey="companySettings.newPrompt">New Prompt</Trans>
                </Button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col h-full`}>
            {isModal && (
                <div className="flex w-full justify-between items-center p-4 px-6 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center gap-2">
                        <img src={`/img/socials/${network}.svg`} className="w-6 h-6" alt={network} />
                        <h3 className="text-lg font-semibold capitalize">{network}</h3>
                    </div>
                    <Button
                        variant="twoTone"
                        size="sm"
                        className="-mb-0.5"
                        icon={<TbSettings />}
                        onClick={() => {
                            navigate(`/company-settings/prompts`);
                            onClose();
                        }}
                    >
                        Prompts Config
                    </Button>
                </div>
            )}
            <div className={`flex-grow space-y-10 py-4 ${isModal ? 'px-6' : ''}`}>
                {/* Followr Prompts */}
                {prompts.filter(p => !p.company_id).length > 0 && (
                    <div>
                        <div className='flex items-center justify-between gap-4 mb-4'>
                            <h5 className="pl-2 text-tertiary-500 dark:text-gray-400 text-lg">
                                <Trans i18nKey="companySettings.followrPrompts">{whitelabel?.name} Prompts</Trans>
                            </h5>
                            {selectable && (
                                <span className='text-primary-600 text-sm font-medium'>
                                    Selected: {selected.filter(p => !p.company_id).length} / {prompts.filter(p => !p.company_id).length}
                                </span>
                            )}
                        </div>
                        <div className={`flex overflow-x-auto gap-4 py-0.5 px-0.5`}>
                            {prompts.filter(p => !p.company_id).map(prompt => (
                                <PromptItem
                                    key={prompt.id}
                                    prompt={prompt}
                                    selectable={selectable}
                                    selected={selected.find(p => p.id === prompt.id)}
                                    onSelect={() => handleSelect(prompt)}
                                    onAdd={handleAddPrompt}
                                    className="!w-[300px] shrink-0"
                                />
                            ))}
                        </div>
                    </div>
                )}
                {/* Company Prompts */}
                <div>
                    <div className='flex items-center justify-between gap-4 mb-4'>
                        <div className="flex items-center gap-4">
                            <h5 className="pl-2 text-tertiary-500 dark:text-gray-400 text-lg">
                                <Trans i18nKey="companySettings.companyPrompts">My Prompts</Trans>
                            </h5>
                            {!selectable && 
                                <Button
                                    variant="twoTone"
                                    size="xs"
                                    className="-mb-0.5"
                                    icon={<BsPlusCircle />}
                                    onClick={() => handleAddPrompt()}
                                    loading={isCreating}
                                    disabled={isCreating}
                                >
                                    <Trans i18nKey="companySettings.newPrompt">New Prompt</Trans>
                                </Button>
                            }
                        </div>
                        {selectable && (
                            <span className='text-primary-600 text-sm font-medium'>
                                Selected: {selected.filter(p => !!p.company_id).length} / {prompts.filter(p => !!p.company_id).length}
                            </span>
                        )}
                    </div>
                    <div className={`flex overflow-x-auto gap-4 py-0.5 px-0.5`}>
                        {prompts.filter(p => !!p.company_id).map(prompt => (
                            <PromptItem
                                key={prompt.id}
                                prompt={prompt}
                                selectable={selectable}
                                selected={selected.find(p => p.id === prompt.id)}
                                onSelect={() => handleSelect(prompt)}
                                refresh={fetchCompanyPrompts}
                                isEditingByDefault={prompt.isNew}
                                onAdd={handleAddPrompt}
                                className="!w-[300px] shrink-0"
                            />
                        ))}
                    </div>
                </div>
            </div>

            {isModal && (
                <div className="px-6 py-4 border-t flex justify-end items-center gap-4">
                    <Button
                        variant="plain"
                        className=""
                        onClick={() => onClose()}
                    >
                        <Trans i18nKey="common.cancel">Cancel</Trans>
                    </Button>
                    <Button
                        variant="solid"
                        onClick={() => onConfirm(selected)}
                        disabled={selected.length === 0}
                        icon={<TbCircleCheck />}
                    >
                        <Trans i18nKey="common.confirm">Confirm</Trans>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Prompts;
